/**
 * Viewport culling — the O(n) AABB filter that decides which objects get
 * recorded into the static SkPicture.
 *
 * Runs on the JS thread ONLY when the camera settles (gesture end,
 * debounced) — never per frame. The recorded picture already includes a
 * viewport margin (`CULL_MARGIN_FACTOR`), so small pans re-blit the existing
 * picture and no cull is needed until the visible rect escapes the margin.
 *
 * Scale path: this linear filter is fine into the low thousands; if
 * profiling ever shows it hot, swap the inner loop for a spatial-grid index
 * (`utils/spatialIndex.ts`, to be added) without touching callers.
 *
 * @module floorplan/utils/culling
 * @category FloorPlan
 */

import type { Bounds } from '../models/geometry';
import type { FloorObject } from '../models/object';
import { boundsIntersect, objectAabb } from './bounds';

/**
 * Filter to the objects whose world AABB intersects `viewBounds`.
 * `viewBounds === null` means "no culling" (whole plan visible / first
 * record before layout) and returns the input array unchanged.
 */
export const cullObjects = (
  objects: readonly FloorObject[],
  viewBounds: Bounds | null,
): readonly FloorObject[] => {
  if (viewBounds === null) return objects;
  const visible: FloorObject[] = [];
  for (const object of objects) {
    if (boundsIntersect(objectAabb(object), viewBounds)) {
      visible.push(object);
    }
  }
  return visible;
};
